// D:\inmueblesYucatan\frontend\src\components\MapboxSelector.jsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin } from 'lucide-react';

const MAPBOX_TOKEN = 'pk.eyJ1IjoiZW5hcnRyYXoiLCJhIjoiY21rcTViYnh6MG90djNlcGxxNmd6dTcwZSJ9.thqCqMAsCvfBfaDdQCYXsg'; // Mismo token que en MapboxPlano

export default function MapboxSelector({ onSeleccionarUbicacion, coordenadasIniciales }) {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [direccion, setDireccion] = useState('');
const hasCenteredRef = useRef(false);

  // Coordenadas iniciales: Centro de Mérida
  const defaultCoords = [-89.592585, 20.967370];
  const [coordenadas, setCoordenadas] = useState(coordenadasIniciales || defaultCoords);

  // Cargar Mapbox
  const loadMapbox = useCallback(async () => {
    if (window.mapboxgl) return;

    try {
      // Cargar CSS
      const link = document.createElement('link');
      link.href = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);

      // Cargar JS
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js';
        script.async = true;
        
        script.onload = () => {
          window.mapboxgl.accessToken = MAPBOX_TOKEN;
          resolve();
        };
        
        script.onerror = reject;
        document.head.appendChild(script);
      });
    } catch (err) {
      console.error('Error cargando Mapbox:', err);
    }
  }, []);

  // Obtener dirección desde coordenadas (geocoding inverso)
  const obtenerDireccion = useCallback(async (coords) => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${coords[0]},${coords[1]}.json?access_token=${MAPBOX_TOKEN}&language=es`
      );
      
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const direccionCompleta = data.features[0].place_name_es || data.features[0].place_name;
        setDireccion(direccionCompleta);
        onSeleccionarUbicacion(coords, direccionCompleta);
      } else {
        setDireccion('Dirección no encontrada');
        onSeleccionarUbicacion(coords, '');
      }
    } catch (error) {
      console.error('Error obteniendo dirección:', error);
      setDireccion('Error obteniendo dirección');
      onSeleccionarUbicacion(coords, '');
    }
  }, [onSeleccionarUbicacion]);

  // Inicializar mapa
  const initMap = useCallback(() => {
    if (!mapContainer.current || !window.mapboxgl) return;

    const map = new window.mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: coordenadas,
      zoom: 14,
      attributionControl: false
    });

    mapRef.current = map;

    const marker = new window.mapboxgl.Marker({
      draggable: true,
      color: '#3b82f6'
    })
      .setLngLat(coordenadas)
      .addTo(map);

    markerRef.current = marker;

    marker.on('dragend', () => {
      const lngLat = marker.getLngLat();
      const newCoords = [lngLat.lng, lngLat.lat];
      setCoordenadas(newCoords);
      obtenerDireccion(newCoords);
    });

    map.on('click', (e) => {
      const newCoords = [e.lngLat.lng, e.lngLat.lat];
      marker.setLngLat(newCoords);
      setCoordenadas(newCoords);
      obtenerDireccion(newCoords);
    });

    map.on('load', () => {
      setMapLoaded(true);
    });

    map.addControl(new window.mapboxgl.NavigationControl(), 'top-right');
  }, [coordenadas, obtenerDireccion]);

  useEffect(() => {
    loadMapbox().then(() => {
      if (window.mapboxgl) {
        initMap();
      }
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, [loadMapbox, initMap]);

  // Actualizar marcador si cambian las coordenadas iniciales
useEffect(() => {
  if (
    !coordenadasIniciales ||
    !markerRef.current ||
    !mapRef.current ||
    hasCenteredRef.current
  ) {
    return;
  }

  markerRef.current.setLngLat(coordenadasIniciales);
  mapRef.current.setCenter(coordenadasIniciales); // ❌ NO flyTo
  mapRef.current.setZoom(15);                     // opcional

  setCoordenadas(coordenadasIniciales);
  obtenerDireccion(coordenadasIniciales);

  hasCenteredRef.current = true; // 🔒 solo una vez
}, [coordenadasIniciales, obtenerDireccion]);

  return (
    <div className="relative w-full h-full">
      {/* Contenedor del mapa */}
      <div 
        ref={mapContainer} 
        className="absolute inset-0 w-full h-full"
      />
      
      {/* Overlay de carga */}
      {!mapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-sm text-slate-600">Cargando mapa...</p>
          </div>
        </div>
      )}
      
      {/* Instrucciones */}
      <div className="absolute top-4 left-4 z-10">
        <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg border border-slate-200 max-w-xs">
          <div className="flex items-center gap-2 mb-2">
            <MapPin size={16} className="text-blue-600" />
            <span className="text-sm font-medium text-slate-900">Selecciona ubicación</span>
          </div>
          <p className="text-xs text-slate-600">
            Haz click en el mapa o arrastra el marcador azul
          </p>
          {direccion && (
            <p className="text-xs text-blue-600 mt-2 truncate" title={direccion}>
              📍 {direccion}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
